import React, { useMemo, useState } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  ErrorAlert,
  Icon,
  Tabs,
  Typography,
} from '@liscio/ui';

import { Loading, MessagesList } from 'components';
import {
  useInfinitiveMessages,
  useMessageCounts,
} from 'fetch-utils/messages/messages-hooks';
import {
  DASHBOARD_DOMAIN,
  DASHBOARD_PATHS,
} from 'modules/dashboard/route-constants';
import {
  MESSAGES_DOMAIN,
  MESSAGES_PATHS,
} from 'modules/messages/route-constants';
import { buildComposePath } from 'modules/messages/utils/composeContext';
import { extractValues } from 'modules/messages/views/ListView/helpers';
import { SMS_DOMAIN, SMS_PATHS } from 'modules/sms/route-constants';
import { routes } from 'routes';

const MESSAGES_LIMIT = 5;
type DashboardMessageTab = 'inbox' | 'alert';

export const DashboardMessages: React.FC = () => {
  const navigate = useNavigate();
  const { newMessageChannelFeature } = useFlags();
  const [activeTab, setActiveTab] = useState<DashboardMessageTab>('inbox');
  const {
    data: infinitiveMessageData,
    isLoading: isMessagesLoading,
    isRefetching: areMessagesRefetching,
    error,
  } = useInfinitiveMessages(activeTab, '');
  const { data: messageCounts } = useMessageCounts();
  const hasError = Boolean(error);
  const { messages } = extractValues(infinitiveMessageData, activeTab, error);
  const inboxNumberOfMessages = messageCounts?.inbox_count;
  const alertsNumberOfMessages = messageCounts?.alert_count;
  const limitedMessages = messages ? messages.slice(0, MESSAGES_LIMIT) : [];
  const emptyLabel =
    activeTab === 'alert' ? 'Your alerts are empty.' : 'Your inbox is empty.';
  const tabs = useMemo(
    () => [
      {
        label: newMessageChannelFeature ? 'Liscio Messages' : 'Inbox',
        value: 'inbox',
        count: inboxNumberOfMessages,
        isCountVisible: true,
        dataTestId: 'dashboard-messages-tab-inbox',
      },
      ...(newMessageChannelFeature
        ? [
            {
              label: 'SMS Messages',
              value: 'sms',
              dataTestId: 'dashboard-messages-tab-sms',
            },
            {
              label: 'Client Emails',
              value: 'emails',
              dataTestId: 'dashboard-messages-tab-emails',
            },
          ]
        : []),
      {
        label: 'Alerts',
        value: 'alert',
        count: alertsNumberOfMessages,
        isCountVisible: true,
        dataTestId: 'dashboard-messages-tab-alert',
      },
    ],
    [alertsNumberOfMessages, inboxNumberOfMessages, newMessageChannelFeature]
  );
  const dashboardReturnState = {
    goBackUrl: `/${DASHBOARD_DOMAIN}${DASHBOARD_PATHS.home}`,
    tab: activeTab,
  };

  return (
    <Box component="section" aria-label="Messages" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          sx={{ flex: '1 1 35rem', minWidth: 0 }}
          ariaLabel="Dashboard message tabs"
          options={tabs}
          selectedOptionValue={activeTab}
          onNavigationClick={(key) => {
            if (key === 'sms') {
              navigate(`/${SMS_DOMAIN}${SMS_PATHS.list}`);
              return;
            }

            if (key === 'emails') {
              navigate(routes.emailInbox.link());
              return;
            }

            setActiveTab((key || 'inbox') as DashboardMessageTab);
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            startIcon={<Icon icon="add" />}
            onClick={() => {
              navigate(
                buildComposePath({ returnTo: dashboardReturnState.goBackUrl }),
                {
                  state: dashboardReturnState,
                }
              );
            }}
          >
            Message
          </Button>
          <Button
            onClick={() => {
              navigate(
                `/${MESSAGES_DOMAIN}${MESSAGES_PATHS.list}?type=${activeTab}&page=1`
              );
            }}
          >
            See All
          </Button>
        </Box>
      </Box>
      {/* Keep long subjects from setting the dashboard's intrinsic width. */}
      <Box sx={{ position: 'relative', minHeight: 100, contain: 'inline-size' }}>
        {(isMessagesLoading || areMessagesRefetching) && (
          <Loading numberOfElements={1} />
        )}
        {!isMessagesLoading && hasError && (
          <Box p={2}>
            <ErrorAlert error={error as Error} />
          </Box>
        )}
        {!isMessagesLoading && !hasError && limitedMessages.length > 0 && (
          <MessagesList
            messages={limitedMessages}
            highlightUnreadComments
            navigateState={dashboardReturnState}
            showDashboardMetadata
          />
        )}
        {!isMessagesLoading && !hasError && limitedMessages.length === 0 && (
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <Typography variant="body1">{emptyLabel}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardMessages;
